// client/src/main.tsx

import "./global.css";
import "./styles/glove-mode.css";
import "./styles/riding-mode.css";
// BUNDLE (Ott 2026): il CSS di Leaflet NON va importato qui. Questo import
// trascinava l'intero chunk `leaflet` (~160 KB) nel caricamento iniziale e
// nell'HTML (`modulepreload`) anche per chi non apre mai una mappa. Ora il CSS
// viaggia con i moduli che usano Leaflet (RouteEditor, RoutePreview,
// leafletMapService, preview-map-main), che sono tutti lazy.
// FASE 9: maplibre-gl CSS loaded dynamically by MapLibreMap component (CDN)
import React from "react";
import ReactDOM from "react-dom/client";
import { Capacitor } from "@capacitor/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "@/App";
import { AppErrorBoundarySimple } from "@/components/ui/ErrorBoundary";
import { GloveModeProvider } from "@/contexts/GloveModeContext";
import { ToastProvider } from "@/components/ui/Toast";
import { configureApiFetch } from "@/api/core/apiFetch";
import { getAuthStoreSnapshot } from "@/api/core/boundApiFetch";
import { registerOfflineCacheBridge } from "@/services/offlineCacheBridge";
// FEAT CONTENT-READS: tap su notizia/broadcast da SW = lettura
import { extractReadableContent, markContentRead } from "@/services/contentReads";
// FIX CHUNK-SW (Set 2026): recovery dai fallimenti dei chunk lazy causati
// dal Service Worker con index.html stantio (es. CerchiaPage/Chat crash).
import { installChunkRecovery } from "@/utils/chunkRecovery";
import { installCspViolationLogger } from "@/utils/cspViolationLogger";

// FASE 3.7: i18n internazionalizzazione (italiano default, inglese disponibile)
import "@/i18n/config";

// ── Sentry client-side crash reporting (lazy: non blocca il render) ─────
const sentryDsn = (import.meta as any).env?.VITE_SENTRY_DSN || "";
if (sentryDsn) {
  import("@sentry/react")
    .then((Sentry) => {
      Sentry.init({
        dsn: sentryDsn,
        environment: (import.meta as any).env?.MODE || "development",
        release: "rideconnect@1.0.0",
        integrations: [
          Sentry.browserTracingIntegration?.(),
          Sentry.replayIntegration?.({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ].filter(Boolean),
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event) {
          if ((import.meta as any).env?.MODE === "development") return null;
          return event;
        },
      });
    })
    .catch(() => {});
}

// ── Web Vitals monitoring (lazy: non blocca il render) ──────────────────
import("@/lib/webVitals").catch(() => {});

// ── Bootstrap API layer ───────────────────────────────────────────────────────
// Registra il getter dello store PRIMA di qualsiasi render/chiamata API.
// apiFetch userà questo come default quando getStore non viene passato
// esplicitamente (tutti i callsite esistenti restano invariati).
configureApiFetch(getAuthStoreSnapshot);

// ── FEAT CONTENT-READS (16 Set 2026): il Service Worker segnala il tap su
// una notifica (notification-click). Se è una notizia/broadcast admin,
// marca la lettura → l'admin vede "chi ha letto" nel pannello.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    const msg = event?.data;
    if (!msg || msg.type !== "notification-click") return;
    const readable = extractReadableContent(msg.data);
    if (readable) {
      void markContentRead(readable.kind, readable.id);
    }
  });
}

// FIX CHUNK-SW (Set 2026): installa SUBITO i listener di recovery —
// prima del render, così ogni import() dinamico fallito è intercettato.
installChunkRecovery();

// FIX CSP-DIAGNOSI (Nov 2026): i blocchi CSP di Chrome compaiono in console
// con URL spesso redatti; qui vengono riportati una volta sola con l'URL
// reale e la direttiva violata.
installCspViolationLogger();

// ── Native Push Notifications (Android/iOS via Capacitor) ─────────────────────
// FIX DOPPIO-INIT (Ago 2026): l'inizializzazione FCM è gestita SOLO da App.tsx
// (dopo il login, con resync token al cambio account). Qui NON la chiamiamo
// più: il doppio register() causava listener duplicati.
// import { initNativePush } from "@/services/nativePush";
// initNativePush().catch((err) => {
//   console.warn("[main] Native push init failed (non-critical):", err);
// });

// ── Status Bar nativa: arancione brand come la webapp ─────────────────────────
// FIX #PUSH-B7 (Ago 2026): il bordo superiore dell'app nativa era blu scuro
// (#0f172a) mentre la webapp è arancione (#FF6B35, theme-color di index.html).
// Oltre alla config Capacitor (cap synced nel progetto nativo), la applichiamo
// anche a runtime così il colore è garantito indipendentemente dalla build.
if ((window as any)?.Capacitor?.isNativePlatform?.()) {
  import("@capacitor/status-bar")
    .then(async ({ StatusBar, Style }) => {
      try {
        await StatusBar.setBackgroundColor({ color: "#FF6B35" });
        await StatusBar.setStyle({ style: Style.Dark });
      } catch (err) {
        console.warn("[main] StatusBar style failed (non-critical):", err);
      }
    })
    .catch(() => {});
}

// ── React Query ───────────────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minuti
      retry: 1,
    },
  },
});

// OFFLINE (Ott 2026): le azioni in coda devono essere visibili anche dove i
// dati arrivano da cache (Dexie/IndexedDB e query attive), non solo nel banner.
registerOfflineCacheBridge(queryClient);

// ── Global Crash Handler (DEBUG) ─────────────────────────────────────────────
// Cattura errori fatali non gestiti e li mostra a schermo invece del blank screen.
// In produzione, Sentry li cattura automaticamente.
window.addEventListener("error", (event) => {
  const msg = event.error?.stack || event.message || "Errore sconosciuto";
  console.error("[FATAL]", msg);
  const root = document.getElementById("root");
  if (root && !root.innerHTML?.trim()) {
    root.innerHTML = `<div style="color:#f87171;padding:20px;font-family:monospace;background:#1a1a2e;min-height:100vh">
      <h2 style="color:#f97316">⚠️ RideConnect — Errore di Avvio</h2>
      <pre style="white-space:pre-wrap;font-size:12px;margin-top:16px">${msg}</pre>
      <p style="margin-top:16px;color:#9ca3af">Prova a riavviare l'app o contatta il supporto.</p>
    </div>`;
  }
});

window.addEventListener("unhandledrejection", (event) => {
  console.error("[FATAL Promise]", event.reason?.stack || event.reason);
});

// ── Mount ─────────────────────────────────────────────────────────────────────
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <AppErrorBoundarySimple>
      <QueryClientProvider client={queryClient}>
        <GloveModeProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </GloveModeProvider>
      </QueryClientProvider>
    </AppErrorBoundarySimple>
  </React.StrictMode>,
);

// Sprint 3: Nascondi splash screen dopo che React ha fatto il primo render
// Con logica intelligente: su Capacitor (Android) se già loggato, nascondi subito
setTimeout(() => {
  const splash = document.getElementById("splash");
  if (!splash) return;

  // Su Capacitor nativo, se l'utente ha un token salvato nel localStorage, skip più veloce
  const isCapacitor = !!(window as any).Capacitor?.isNativePlatform?.();
  const hasToken =
    !!localStorage.getItem("rc-auth-token") || !!localStorage.getItem("token");
  const minDelay = isCapacitor && hasToken ? 300 : 2500; // 300ms se già loggato su app, 2.5s altrimenti

  const elapsed = Date.now() - (window as any).__splashStart;
  const remaining = Math.max(0, minDelay - elapsed);

  setTimeout(() => {
    splash?.classList.add("hide");
    setTimeout(() => splash?.remove(), 500);
  }, remaining);
}, 200);

// Registra timestamp inizio
(window as any).__splashStart = Date.now();

// ── Service Worker ────────────────────────────────────────────────────────────
// FIX CRITICO: Su Capacitor (Android/iOS) il Service Worker NON deve essere
// registrato. Intercetta le richieste API e tile causando cache di risposte
// 429, risposte opaque per tile cross-origin, e interferenze con il
// funzionamento nativo della WebView.
//
// Rileviamo Capacitor dal protocol/hostname: la WebView carica da
// https://localhost, mentre il browser web usa un dominio pubblico.
// Questo è PIÙ affidabile di Capacitor.isNativePlatform() che può
// restituire false se il plugin non è ancora inizializzato.
const isLocalhost =
  typeof window !== "undefined" && window.location?.hostname === "localhost";
console.log(
  "[SW DEBUG] isLocalhost:",
  isLocalhost,
  "hostname:",
  typeof window !== "undefined" ? window.location?.hostname : "N/A",
);

if ("serviceWorker" in navigator) {
  if (isLocalhost) {
    // Capacitor WebView: RIMUOVI AGGRESSIVAMENTE ogni Service Worker.
    // Il SW persiste tra gli aggiornamenti dell'app (finché non si
    // cancellano i dati), quindi dobbiamo unregister esplicitamente.
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const reg of registrations) {
        reg.unregister().then(() => {
          console.log("[SW] Rimosso su Capacitor:", reg.scope);
        });
      }
    });
  } else if (import.meta.env.PROD) {
    // Browser web: registra il SW normalmente (PWA).
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          console.log("[SW] Registered, scope:", reg.scope);

          // Sprint 3: Toast "Nuova versione disponibile"
          reg.addEventListener("updatefound", () => {
            const newWorker = reg.installing;
            if (!newWorker) return;
            newWorker.addEventListener("statechange", () => {
              if (
                newWorker.state === "installed" &&
                navigator.serviceWorker.controller
              ) {
                const toast = document.createElement("div");
                toast.style.cssText =
                  "position:fixed;bottom:100px;left:16px;right:16px;z-index:99999;" +
                  "background:#FF6B35;color:#fff;padding:12px 16px;border-radius:12px;" +
                  'text-align:center;font-family:"Inter Tight",sans-serif;font-weight:600;' +
                  "box-shadow:0 4px 24px rgba(0,0,0,0.5);cursor:pointer;animation:slideUp 0.3s ease-out;";
                toast.textContent =
                  "🔄 Nuova versione disponibile — tocca per aggiornare";
                toast.addEventListener("click", () => {
                  newWorker.postMessage({ type: "SKIP_WAITING" });
                  window.location.reload();
                });
                document.body.appendChild(toast);
                setTimeout(() => toast.remove(), 15000);
              }
            });
          });
        })
        .catch((err) => {
          console.warn("[SW] Registrazione fallita:", err);
        });
    });
  }
}

// Animazione toast
const toastStyle = document.createElement("style");
toastStyle.textContent =
  "@keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }";
document.head.appendChild(toastStyle);
